"use client";

import React, { useState, useEffect } from "react";
import { Phone, Mail, Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="29903" className="hidden lg:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent text-[14px] font-medium font-sans text-white hover:text-primary transition-colors h-auto p-0 uppercase tracking-wider">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[200px] gap-1 p-2 bg-black/95 backdrop-blur-md rounded-md shadow-2xl border border-[#333333]">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-white/10 hover:text-primary focus:bg-white/10 focus:text-primary text-[14px] font-sans text-white">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="text-[14px] font-medium font-sans text-white hover:text-primary transition-colors block uppercase tracking-wider relative group">
                  {item.label}
                  <span className="absolute -bottom-1 left-1/2 w-0 h-[2px] bg-primary transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <nav className="flex flex-col space-y-4 p-6 bg-black">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-[#333333] pb-2 last:border-0">
          {item.children && item.children.length > 0 ? <div className="space-y-2">
              <span className="font-bold text-lg font-serif text-primary block mb-2 uppercase tracking-tight">
                {item.label}
              </span>
              <div className="pl-4 flex flex-col space-y-3 border-l-2 border-primary/40">
                {item.children.map((child, j) => <Link key={child.id} data-index={j} to={child.href || "#"} className="text-base font-sans text-white/70 hover:text-primary transition-colors block" onClick={onClose}>
                    {child.label}
                  </Link>)}
              </div>
            </div> : <Link to={item.href || "#"} className="block text-lg font-bold font-serif text-white hover:text-primary transition-colors uppercase tracking-tight" onClick={onClose}>
              {item.label}
            </Link>}
        </div>)}
    </nav>;
}
export default function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className="w-full flex flex-col font-sans dark">
      {}
      <div className="w-full h-[40px] bg-black text-white flex items-center justify-end px-4 lg:px-8 text-[14px] font-sans z-50 relative border-b border-[#333333]">
        <div className="flex items-center gap-6">
          <Link to="tel:+13059356066" className="flex items-center gap-2 hover:text-primary transition-colors">
            <Phone className="size-3 text-primary" />
            <span>(305) 935.6066</span>
          </Link>
          <Link to="mailto:info@tmprosthodontics.com" className="flex items-center gap-2 hover:text-primary transition-colors">
            <Mail className="size-3 text-primary" />
            <span>info@elprosthodontics.com</span>
          </Link>
        </div>
      </div>

      {}
      <header className={cn("sticky top-0 z-40 w-full transition-all duration-300 border-b", isScrolled ? "bg-black/80 backdrop-blur-lg border-[#333333] shadow-xl" : "bg-black border-transparent")}>
        <div className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
          {}
          <Link to="/HomePage" className="flex-shrink-0">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606922/img/pixelmart_logo.png" alt="TM Prosthodontics Logo" className="h-12 w-auto object-contain brightness-0 invert" />
          </Link>

          {}
          <div className="hidden lg:flex items-center gap-8">
            <MenuProvider menu_id="41">
              <DesktopMenuConsumer />
            </MenuProvider>

            {}
            <div className="pl-4 border-l border-[#333333]">
              <Link to="/HomePage" className="text-primary font-serif italic text-sm">
                Excellence in Dentistry
              </Link>
            </div>
          </div>

          {}
          <div className="flex lg:hidden items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-primary hover:bg-transparent">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>

        {}
        {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-black border-b border-[#333333] shadow-2xl animate-in slide-in-from-top-5 duration-200 max-h-[calc(100vh-120px)] overflow-y-auto">
            <MenuProvider menu_id="41">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>}
      </header>
    </section>;
}